import { message } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { serializeMainInfoForStorage } from 'pages/reporting/reports/rug/hooks/useFormData'
import { RugFormDataState } from 'pages/reporting/reports/rug/rugReportData'
import { usePublishRevisionHandler } from 'pages/reporting/shared/hooks/usePublishRevisionHandler'
import { useSignoffHandler } from 'pages/reporting/shared/hooks/useSignoffHandler'
import { useCallback, useState } from 'react'
import { SignatureData } from 'types'

interface UseReportSubmissionProps {
  metadataEntryId: string
  formData: RugFormDataState
  updateFormData: (data: Partial<RugFormDataState>) => void
  currentRevisionNumber: number
  pdfFilename: string
  publishLatestDraftPDF: (pdfPath: string) => Promise<void>
  publishRevision: () => Promise<string>
}

interface UseReportSubmissionResult {
  isSubmitting: boolean
  handleSubmitReport: () => Promise<void>
  handleSignoff: (signatureData: SignatureData) => Promise<void>
  handlePublishRevision: (dataToUse?: RugFormDataState) => Promise<void>
}

export const useReportSubmission = ({
  metadataEntryId,
  formData,
  updateFormData,
  currentRevisionNumber,
  pdfFilename,
  publishLatestDraftPDF,
  publishRevision,
}: UseReportSubmissionProps): UseReportSubmissionResult => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { rolodexPatch } = useRolodexEdit()

  const handleSubmitReport = useCallback(async () => {
    if (!metadataEntryId) {
      message.error('Metadata entry not yet created')
      return
    }

    try {
      setIsSubmitting(true)

      const reportData = {
        main_info: serializeMainInfoForStorage(formData.mainInfo),
        section_info: {
          coverSectionData: formData.coverSectionData,
          summarySectionData: formData.summarySectionData,
          dataSheetSectionData: formData.dataSheetSectionData,
          imagesSectionData: formData.imagesSectionData,
          assetDrawings: formData.assetDrawings,
          sectionAddendums: formData.sectionAddendums,
          addendumDraft: formData.addendumDraft,
        },
      }

      await rolodexPatch([
        {
          id: metadataEntryId,
          data: reportData,
        },
      ])

      message.success('Report data submitted successfully!')
    } catch (error) {
      console.error('Failed to submit report:', error)
      message.error('Failed to submit report')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }, [metadataEntryId, formData, rolodexPatch])

  const generatePdfPath = useCallback(
    (timestamp: string) => `reportPDF/${pdfFilename}-${timestamp}.pdf`,
    [pdfFilename]
  )

  const { handlePublishRevision } = usePublishRevisionHandler({
    metadataEntryId,
    formData,
    currentRevisionNumber,
    publishLatestDraftPDF,
    publishRevision,
    setIsSubmitting,
    generatePdfPath,
  })

  const { handleSignoff } = useSignoffHandler({
    metadataEntryId,
    formData,
    updateFormData,
    setIsSubmitting,
    handlePublishRevision,
  })

  return {
    isSubmitting,
    handleSubmitReport,
    handleSignoff,
    handlePublishRevision,
  }
}
